import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useMemo, useState } from 'react'
import useSWR, { mutate } from 'swr'
import { apiURI } from '../utils/constants'
import { getRequest, putRequest, staticOptions } from '../core/api-client'
import {
  BannerConfig,
  CouponVisibilityConfig,
  PaymentPageConfig,
  PaymentPageConfigBEType,
  PaymentTileConfig
} from '../types/paymentPage'
import { SubscriptionConfig } from '../types/subscriptions'
import {
  createPaymentModesPayload,
  createPaymentUiAttributesPayload,
  defaultBannerConfig,
  defaultCouponVisibilityConfig,
  defaultPaymentConfig,
  parsePaymentConfig
} from '../utils/payment-page'
import { compareStringConvertValue } from '../utils/helpers'
import { PaymentModeType } from '../types/merchantAttributes'
import { successToast, errorToast } from '../utils/toasters'

export enum PaymentPageActionType {
  SET_CHECKOUT_CUSTOMIZATION_DATA = 'SET_CHECKOUT_CUSTOMIZATION_DATA',
  SET_COUPON_VISIBILITY_CONFIG = 'SET_COUPON_VISIBILITY_CONFIG',
  RESET_CHANGES = 'RESET_CHANGES',
  SET_BANNER_CONFIG = 'SET_BANNER_CONFIG',
  SET_PAYMENT_TILE_CONFIG = 'SET_PAYMENT_TILE_CONFIG',
  SET_SELECTION = 'SET_SELECTION',
  SET_HOVERED_METHOD = 'SET_HOVERED_METHOD',
  SET_SUBSCRIPTION_CONFIG = 'SET_SUBSCRIPTION_CONFIG',
  SET_PURCHASE_MODE = 'SET_PURCHASE_MODE',
  SET_DEVICE_TYPE = 'SET_DEVICE_TYPE'
}

export type PurchaseModeTab = 'PAY_ONCE' | 'SUBSCRIBE'

type SelectionState = { type: 'BANNER' | 'METHOD' | 'LAYOUT' | 'COUPON' | null; id: string | null }

type PaymentPageAction =
  | {
      type: PaymentPageActionType.SET_CHECKOUT_CUSTOMIZATION_DATA
      payload: {
        checkoutCustomizationData: PaymentPageConfigBEType
        paymentModes: PaymentModeType[]
      }
    }
  | {
      type: PaymentPageActionType.SET_COUPON_VISIBILITY_CONFIG
      payload: CouponVisibilityConfig
    }
  | {
      type: PaymentPageActionType.RESET_CHANGES
    }
  | {
      type: PaymentPageActionType.SET_BANNER_CONFIG
      payload: BannerConfig
    }
  | {
      type: PaymentPageActionType.SET_PAYMENT_TILE_CONFIG
      payload: PaymentTileConfig
    }
  | {
      type: PaymentPageActionType.SET_SELECTION
      payload: SelectionState
    }
  | {
      type: PaymentPageActionType.SET_HOVERED_METHOD
      payload: string | null
    }
  | {
      type: PaymentPageActionType.SET_SUBSCRIPTION_CONFIG
      payload: SubscriptionConfig
    }
  | {
      type: PaymentPageActionType.SET_PURCHASE_MODE
      payload: PurchaseModeTab
    }
  | {
      type: PaymentPageActionType.SET_DEVICE_TYPE
      payload: 'desktop' | 'mobile'
    }

export interface PaymentPageState {
  checkoutCustomizationData: PaymentPageConfig
  initialCheckoutCustomizationData: PaymentPageConfig
  selection: SelectionState
  hoveredMethodId: string | null
  purchaseMode: PurchaseModeTab
  deviceType: 'desktop' | 'mobile'
}

const initialState: PaymentPageState = {
  checkoutCustomizationData: defaultPaymentConfig,
  initialCheckoutCustomizationData: defaultPaymentConfig,
  selection: { type: 'LAYOUT', id: null },
  hoveredMethodId: null,
  purchaseMode: 'PAY_ONCE',
  deviceType: 'desktop'
}

function paymentPageReducer(state: PaymentPageState, action: PaymentPageAction): PaymentPageState {
  switch (action.type) {
    case PaymentPageActionType.SET_CHECKOUT_CUSTOMIZATION_DATA:
      const parsedData = parsePaymentConfig(action.payload.checkoutCustomizationData, action.payload.paymentModes)
      return {
        ...state,
        checkoutCustomizationData: parsedData,
        initialCheckoutCustomizationData: parsedData
      }
    case PaymentPageActionType.SET_COUPON_VISIBILITY_CONFIG:
      const hasPaymentsConfig = state.checkoutCustomizationData.couponVisibilityConfigs?.some(
        config => config.type === 'PAYMENTS'
      )

      return {
        ...state,
        checkoutCustomizationData: {
          ...state.checkoutCustomizationData,
          couponVisibilityConfigs: hasPaymentsConfig
            ? state.checkoutCustomizationData.couponVisibilityConfigs.map(config =>
                config.type === 'PAYMENTS' ? action.payload : config
              )
            : [...state.checkoutCustomizationData.couponVisibilityConfigs, action.payload]
        }
      }
    case PaymentPageActionType.RESET_CHANGES:
      return {
        ...state,
        checkoutCustomizationData: state.initialCheckoutCustomizationData
      }
    case PaymentPageActionType.SET_BANNER_CONFIG:
      const hasPaymentsBannerConfig = state.checkoutCustomizationData.bannerConfigs?.some(
        config => config.type === action.payload.type
      )

      return {
        ...state,
        checkoutCustomizationData: {
          ...state.checkoutCustomizationData,
          bannerConfigs: hasPaymentsBannerConfig
            ? state.checkoutCustomizationData.bannerConfigs.map(config =>
                config.type === action.payload.type ? action.payload : config
              )
            : [...state.checkoutCustomizationData.bannerConfigs, action.payload]
        }
      }
    case PaymentPageActionType.SET_PAYMENT_TILE_CONFIG:
      return {
        ...state,
        checkoutCustomizationData: {
          ...state.checkoutCustomizationData,
          paymentTileConfigs: action.payload
        }
      }
    case PaymentPageActionType.SET_SELECTION:
      return {
        ...state,
        selection: action.payload
      }
    case PaymentPageActionType.SET_HOVERED_METHOD:
      return {
        ...state,
        hoveredMethodId: action.payload
      }
    case PaymentPageActionType.SET_SUBSCRIPTION_CONFIG:
      return {
        ...state,
        checkoutCustomizationData: {
          ...state.checkoutCustomizationData,
          subscriptionConfig: action.payload
        }
      }
    case PaymentPageActionType.SET_PURCHASE_MODE:
      return { ...state, purchaseMode: action.payload }
    case PaymentPageActionType.SET_DEVICE_TYPE:
      return {
        ...state,
        deviceType: action.payload
      }
    default:
      return state
  }
}

interface PaymentPageContextType {
  state: PaymentPageState
  actions: {
    setCheckoutCustomizationData: (payload: PaymentPageConfigBEType | null) => void
    setCouponVisibilityConfig: (payload: CouponVisibilityConfig) => void
    resetChanges: () => void
    setBannerConfig: (payload: BannerConfig) => void
    setPaymentTileConfig: (payload: PaymentTileConfig) => void
    setSelection: (payload: SelectionState) => void
    setHoveredMethod: (payload: string | null) => void
    setSubscriptionConfig: (payload: SubscriptionConfig) => void
    setPurchaseMode: (payload: PurchaseModeTab) => void
    saveChanges: () => Promise<void>
    setDevice: (payload: 'desktop' | 'mobile') => void
  }
  getters: {
    checkoutCustomizationData: PaymentPageConfig
    bannerConfigs: BannerConfig[]
    couponVisibilityConfigs: CouponVisibilityConfig[]
    bannerConfig: BannerConfig
    couponVisibilityConfig: CouponVisibilityConfig
    hasChanges: boolean
    paymentTileConfigs: PaymentTileConfig
    selection: SelectionState
    hoveredMethodId: string | null
    subscriptionConfig: SubscriptionConfig
    purchaseMode: PurchaseModeTab
    isSaving: boolean
    device: 'desktop' | 'mobile'
  }
}

const PaymentPageContext = createContext<PaymentPageContextType | undefined>(undefined)

const PaymentPageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(paymentPageReducer, initialState)
  const [isSaving, setIsSaving] = useState(false)

  const {
    data: checkoutCustomizationData,
    isLoading,
    mutate: mutateCheckoutCustomizationData
  } = useSWR<PaymentPageConfigBEType>(
    `/md/merchants/ui-attributes?channel=web&sfContext=CHECKOUT`,
    async (url: string) => {
      const response: any = await getRequest(url, apiURI.FLO_KRATOS_CHECKOUT)
      return response?.data as PaymentPageConfigBEType
    },
    staticOptions
  )

  const {
    data: paymentModesResponse,
    isValidating: fetchingPaymentModes,
    mutate: mutatePaymentModesResponse
  } = useSWR(
    `/fms/v1/merchant-payment-modes`,
    async (url: string) => {
      const response: any = await getRequest(url, apiURI.FLO_CHECKOUT)
      return response?.data?.payment_methods as PaymentModeType[]
    },
    staticOptions
  )

  useEffect(() => {
    if (isSaving) return
    if (checkoutCustomizationData && paymentModesResponse) {
      dispatch({
        type: PaymentPageActionType.SET_CHECKOUT_CUSTOMIZATION_DATA,
        payload: { checkoutCustomizationData, paymentModes: paymentModesResponse }
      })
    }
  }, [checkoutCustomizationData, paymentModesResponse, isSaving])

  const saveChanges = async () => {
    setIsSaving(true)
    try {
      const uiAttributesPayload = createPaymentUiAttributesPayload(state)
      const paymentModesPayload = createPaymentModesPayload(
        state.checkoutCustomizationData.paymentTileConfigs.modeDetails,
        state.checkoutCustomizationData.subscriptionConfig.paymentTiles
      )

      // banner_config + coupon_visibility_config are persisted to the CHECKOUT ui-attributes below
      // (the only source checkout-ui reads). We no longer mirror them into the deprecated merchant
      // `ui` block (CHEC-13941), so there's no merchant-attributes write here anymore.
      await Promise.all([
        putRequest('/md/merchants/ui-attributes', apiURI.FLO_KRATOS_CHECKOUT, uiAttributesPayload),
        putRequest('/fms/v1/merchant-gateways/reorder-payment-modes', apiURI.FLO_CHECKOUT, paymentModesPayload)
      ])

      // Awaiting both revalidations together so the isSaving-gated effect only ever sees a consistent, paired update. The save itself already succeeded above, so a revalidation failure here is logged but shouldn't surface as a save failure to the merchant.
      await Promise.all([
        mutateCheckoutCustomizationData().catch(err => console.error('Failed to refresh ui-attributes:', err)),
        mutatePaymentModesResponse().catch(err => console.error('Failed to refresh payment modes:', err))
      ])

      successToast('Payment page customization saved successfully')
    } catch (error: any) {
      console.error('Error saving payment page changes:', error)
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        'Failed to save changes. Please try again.'
      errorToast(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const actions = {
    setCheckoutCustomizationData: (payload: any) => {
      dispatch({ type: PaymentPageActionType.SET_CHECKOUT_CUSTOMIZATION_DATA, payload })
    },
    setCouponVisibilityConfig: (payload: any) => {
      dispatch({ type: PaymentPageActionType.SET_COUPON_VISIBILITY_CONFIG, payload })
    },
    resetChanges: () => {
      dispatch({ type: PaymentPageActionType.RESET_CHANGES })
    },
    setBannerConfig: (payload: any) => {
      dispatch({ type: PaymentPageActionType.SET_BANNER_CONFIG, payload })
    },
    setPaymentTileConfig: (payload: PaymentTileConfig) => {
      dispatch({ type: PaymentPageActionType.SET_PAYMENT_TILE_CONFIG, payload })
    },
    setSelection: (payload: SelectionState) => {
      dispatch({ type: PaymentPageActionType.SET_SELECTION, payload })
    },
    setHoveredMethod: (payload: string | null) => {
      dispatch({ type: PaymentPageActionType.SET_HOVERED_METHOD, payload })
    },
    setSubscriptionConfig: (payload: SubscriptionConfig) => {
      dispatch({ type: PaymentPageActionType.SET_SUBSCRIPTION_CONFIG, payload })
    },
    setPurchaseMode: (payload: PurchaseModeTab) => {
      dispatch({ type: PaymentPageActionType.SET_PURCHASE_MODE, payload })
    },
    setDevice: (payload: 'desktop' | 'mobile') => {
      dispatch({ type: PaymentPageActionType.SET_DEVICE_TYPE, payload })
    },
    saveChanges
  }

  const getters = {
    hasChanges: useMemo(
      () => !compareStringConvertValue(state.checkoutCustomizationData, state.initialCheckoutCustomizationData),
      [state]
    ),
    checkoutCustomizationData: state.checkoutCustomizationData,
    bannerConfigs: state.checkoutCustomizationData.bannerConfigs,
    couponVisibilityConfigs: state.checkoutCustomizationData.couponVisibilityConfigs,
    bannerConfig: state.checkoutCustomizationData.bannerConfigs.find(
      config => config.type === (state.purchaseMode === 'SUBSCRIBE' ? 'PAYMENTS_SUBSCRIBE' : 'PAYMENTS')
    ) ?? {
      ...defaultBannerConfig,
      type: state.purchaseMode === 'SUBSCRIBE' ? 'PAYMENTS_SUBSCRIBE' : 'PAYMENTS'
    },
    couponVisibilityConfig:
      state.checkoutCustomizationData.couponVisibilityConfigs.find(config => config.type === 'PAYMENTS') ??
      defaultCouponVisibilityConfig,
    paymentTileConfigs: state.checkoutCustomizationData.paymentTileConfigs,
    selection: state.selection,
    hoveredMethodId: state.hoveredMethodId,
    subscriptionConfig: state.checkoutCustomizationData.subscriptionConfig,
    purchaseMode: state.purchaseMode,
    isSaving,
    device: state.deviceType
  }

  return <PaymentPageContext.Provider value={{ state, actions, getters }}>{children}</PaymentPageContext.Provider>
}

const usePaymentPage = () => {
  const context = useContext(PaymentPageContext)
  if (context === undefined) {
    throw new Error('usePaymentPage must be used within a PaymentPageProvider')
  }
  return context
}

export { PaymentPageProvider }

export default usePaymentPage
